import { FileText, Download, File as FileIcon, Play, FileX } from 'lucide-react';
import VoiceNotePlayer from './VoiceNotePlayer';

function humanSize(bytes) {
  if (!bytes) return '';
  const units = ['B', 'KB', 'MB', 'GB'];
  let n = bytes; let i = 0;
  while (n >= 1024 && i < units.length - 1) { n /= 1024; i++; }
  return `${n.toFixed(n >= 100 || i === 0 ? 0 : 1)} ${units[i]}`;
}

/**
 * Defence-in-depth for historic/corrupted attachment rows. The backend is the
 * primary trust boundary, but the renderer must never turn an unsafe URL into
 * a clickable or media source even if bad data already exists in MongoDB.
 */
export function isSafeAttachmentUrl(url) {
  const text = String(url || '').trim();
  if (!text) return false;

  try {
    const parsed = new URL(text, window.location.origin);
    const protocol = parsed.protocol.toLowerCase();
    if (parsed.username || parsed.password) return false;
    if (protocol === 'https:') return true;
    if (protocol === 'http:' && ['localhost', '127.0.0.1', '::1'].includes(parsed.hostname)) return true;
    return false;
  } catch (error) {
    return false;
  }
}

/**
 * Legacy attachments were saved to `backend/uploads/…` before the
 * migration to Cloudflare R2. Those URLs no longer resolve. The admin
 * data-hygiene sweep stamps `unavailable: true` on the attachment
 * sub-document so this renderer can show a clear placeholder instead
 * of a broken image. We also detect the raw legacy URL pattern as a
 * belt-and-braces safety net for any row the sweep hasn't reached
 * yet (e.g. immediately after import).
 */
function isAttachmentUnavailable(a) {
  if (!a) return true;
  if (a.unavailable === true) return true;
  const url = a.file_url || '';
  if (!url) return true;
  if (!isSafeAttachmentUrl(url)) return true;
  if (url.startsWith('/uploads/')) return true;
  if (/^https?:\/\/[^/]+\/uploads\//.test(url)) return true;
  return false;
}

function UnavailableAttachmentTile({ attachment, isMine }) {
  const dark = isMine;
  return (
    <div
      className={`flex items-center gap-3 px-3 py-2.5 rounded-xl min-w-[220px] max-w-[320px] border border-dashed ${
        dark
          ? 'bg-white/10 border-white/25 text-white/85'
          : 'bg-theme-surface/60 border-theme text-theme-muted'
      }`}
      data-testid="attachment-unavailable"
      title="This attachment is no longer available"
    >
      <div className={`h-10 w-10 rounded-lg flex items-center justify-center shrink-0 ${
        dark ? 'bg-white/15 text-white/85' : 'bg-theme/60 text-theme-muted'
      }`}>
        <FileX className="h-5 w-5" />
      </div>
      <div className="flex-1 min-w-0">
        <p className={`text-sm font-medium truncate ${dark ? 'text-white' : 'text-theme-text'}`}>
          This attachment is no longer available
        </p>
        <p className={`text-[11px] ${dark ? 'text-white/70' : 'text-theme-muted'}`}>
          {attachment?.file_name
            ? `${(attachment.file_type || 'file').toUpperCase()} · ${attachment.file_name}`
            : 'Legacy file — please ask the sender to re-share.'}
        </p>
      </div>
    </div>
  );
}

/**
 * AttachmentRenderer — renders a single message attachment inline with tasteful
 * styling for images, PDFs, videos, generic files and voice notes.
 *
 * Props:
 *   attachment — { file_name, file_type, file_size, file_url, mime_type, duration_sec, unavailable? }
 *   isMine     — true for messages sent by the current user (tone adjustment)
 *   onOpenImage(url) — optional callback to open image in a lightbox
 */
export default function AttachmentRenderer({ attachment: a, isMine = false, onOpenImage }) {
  if (!a) return null;
  if (isAttachmentUnavailable(a)) {
    return <UnavailableAttachmentTile attachment={a} isMine={isMine} />;
  }
  const dark = isMine;
  const url = a.file_url;

  if (a.file_type === 'image') {
    return (
      <button
        type="button"
        onClick={() => onOpenImage?.(url)}
        className="block rounded-lg overflow-hidden max-w-[260px] sm:max-w-[320px] shadow-sm hover:opacity-95 transition-opacity"
        data-testid="attachment-image"
      >
        <img src={url} alt={a.file_name} className="block w-full h-auto max-h-[320px] object-cover" loading="lazy" />
      </button>
    );
  }

  if (a.file_type === 'audio') {
    return <VoiceNotePlayer url={url} duration={a.duration_sec} tone={dark ? 'dark' : 'light'} />;
  }

  if (a.file_type === 'video') {
    return (
      <div className="rounded-lg overflow-hidden max-w-[320px] bg-black">
        <video src={url} controls className="block w-full h-auto max-h-[320px]" />
      </div>
    );
  }

  const icon = a.file_type === 'pdf' ? (
    <FileText className="h-5 w-5" />
  ) : (
    <FileIcon className="h-5 w-5" />
  );

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      download={a.file_name}
      className={`flex items-center gap-3 px-3 py-2.5 rounded-xl min-w-[220px] max-w-[320px] transition-colors ${
        dark ? 'bg-white/15 hover:bg-white/20 text-white' : 'bg-theme-surface hover:bg-theme-surface/70 text-theme-text'
      }`}
      data-testid="attachment-file"
    >
      <div className={`h-10 w-10 rounded-lg flex items-center justify-center shrink-0 ${
        dark ? 'bg-white/90 text-[#002FA7]' : 'bg-[#002FA7] text-white'
      }`}>
        {a.file_type === 'video' ? <Play className="h-5 w-5" /> : icon}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">{a.file_name}</p>
        <p className={`text-[11px] ${dark ? 'text-white/70' : 'text-theme-muted'}`}>
          {(a.file_type || 'file').toUpperCase()} · {humanSize(a.file_size)}
        </p>
      </div>
      <Download className={`h-4 w-4 shrink-0 ${dark ? 'text-white/80' : 'text-theme-muted'}`} />
    </a>
  );
}
